/**
 * @file SessionCommentMenu.tsx
 * @brief A session's comments as a dropdown under its button, not a dialog.
 *
 * @details
 * DES 04-10-2026: "uske baad comments bhi aise hi add hon" — after tags went
 * into dropdowns (TagsMenu), comments follow: the Home card's pen and "HAS
 * NOTES" tab, and the session page's "Add comment" chip. The menu shows the
 * comments so far (read-only, scrolling) and a box for a new one.
 *
 * ⚠️ `PATCH /api/sessions/:id/notes` APPENDS a signed `[Name - time]` entry
 * to what is there, so only the new text is sent, never the existing notes
 * (the phone's NoteEditModal pre-fills them and duplicates; see the report
 * of 04-10-2026). The route checks `verifySessionAccess`.
 *
 * Typing stays in the box: keys other than Escape and Tab are kept from the
 * menu's typeahead, as SearchableMenuList's search field does.
 *
 * @author DES
 * @date 04-10-2026
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { Button } from "@/components/ui/Button";
import { ApiClientError, apiPatch } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { isRTLText } from "@/shared/utils/formatters";

export function SessionCommentMenu({
  sessionId,
  notes,
  anchor,
  align = "end",
  side,
  sideOffset,
  open: openProp,
  onOpenChange,
}: {
  sessionId: string;
  /** The comments so far (the session's `notes`). */
  notes?: string | null;
  anchor: ReactElement;
  align?: "start" | "center" | "end";
  /** A dock opens it upward with InvoiceMenu's gap (DES 04-10-2026). */
  side?: "top" | "bottom";
  sideOffset?: number;
  /** Controlled open: a dock keeps one menu open at a time (DES 04-10-2026). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (o: boolean) => {
    setOpenState(o);
    onOpenChange?.(o);
  };
  const [draft, setDraft] = useState("");
  const existing = (notes ?? "").trim();
  const next = draft.trim();

  const add = useMutation({
    mutationFn: (note: string) =>
      apiPatch<{ success?: boolean; notes?: string }>(
        `/api/sessions/${encodeURIComponent(sessionId)}/notes`,
        { notes: note },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["home", "sessions"] });
      void qc.invalidateQueries({ queryKey: ["admin", "sessions"] });
      void qc.invalidateQueries({ queryKey: ["company", "sessions"] });
    },
  });

  const submit = () => {
    if (!next || add.isPending) return;
    add.mutate(next, {
      onSuccess: () => {
        setDraft("");
        setOpen(false);
      },
      onError: (err) =>
        toast({
          variant: "destructive",
          title: "Could not save the comment",
          description: err instanceof ApiClientError ? err.message : "Please try again.",
        }),
    });
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setDraft("");
      }}
    >
      <DropdownMenuTrigger asChild>{anchor}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        className="w-80 p-1.5"
        // Portalled, but React bubbles clicks to the trigger's ancestors: on
        // a card that is a link, a tap here would open the session.
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {existing ? "Comments" : "Add a comment"}
        </DropdownMenuLabel>
        {existing ? (
          <p
            dir={isRTLText(existing) ? "rtl" : "ltr"}
            className="mx-1 mb-2 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-xl bg-app-page px-3 py-2 text-[13px] text-app-text-secondary"
          >
            {existing}
          </p>
        ) : null}
        <div className="px-1 pb-1">
          <textarea
            autoFocus
            value={draft}
            rows={3}
            placeholder={existing ? "Add another comment" : "Write a comment"}
            aria-label="Comment"
            dir={isRTLText(draft) ? "rtl" : "ltr"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // ⌘/Ctrl + Enter saves; Escape and Tab still reach the menu.
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
              if (e.key !== "Escape" && e.key !== "Tab") e.stopPropagation();
            }}
            className="w-full resize-none rounded-xl bg-field px-3 py-2 text-[14px] text-app-text outline-none placeholder:text-app-text-secondary/70 focus:ring-2 focus:ring-ring"
          />
          <div className="mt-2 flex items-center justify-end">
            <Button type="button" size="sm" disabled={!next || add.isPending} onClick={submit}>
              {add.isPending ? "Saving…" : "Add comment"}
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
